import React, { useState, useEffect, useRef } from 'react';
import { Bell, Trash } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface NotificationBellProps {
  type: 'superadmin' | 'clientadmin';
}

interface NotificationItem {
  id: string;
  message: string;
  time: string;
  read: boolean;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ type }) => {
  const storageKey = `sthitih_notifications_${type}`;
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load from localstorage
    const localData = localStorage.getItem(storageKey);
    if (localData) {
      try {
        setNotifications(JSON.parse(localData));
      } catch (e) {
        initializeSampleNotifications();
      }
    } else {
      initializeSampleNotifications();
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initializeSampleNotifications = () => {
    const samples: NotificationItem[] = type === 'superadmin' ? [
      { id: '1', message: 'New Client Subscribed: MIT ADT University (Trial Tier)', time: '5 mins ago', read: false },
      { id: '2', message: 'Urgent Support Ticket: Payment mismatch from Southern Campus', time: '1 hr ago', read: false },
      { id: '3', message: 'Daily automated backup completed successfully', time: '1 day ago', read: true }
    ] : [
      { id: '1', message: 'New Team formed: Byte Busters (Computer Science)', time: '10 mins ago', read: false },
      { id: '2', message: 'Budget Claim approved: ₹24,000 for volunteer catering kits', time: '2 hrs ago', read: false },
      { id: '3', message: 'Venue scheduler update: Auditorium reserved for winter hackathon', time: '1 day ago', read: true }
    ];
    setNotifications(samples);
    localStorage.setItem(storageKey, JSON.stringify(samples));
  };

  const saveToStorage = (list: NotificationItem[]) => {
    setNotifications(list);
    localStorage.setItem(storageKey, JSON.stringify(list));
  };

  const markAllRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    saveToStorage(updated);
    toast.success('All notifications marked as read');
  };

  const clearAll = () => {
    saveToStorage([]);
    toast.success('Notification logs cleared');
  };

  const toggleRead = (id: string) => {
    const updated = notifications.map(n => n.id === id ? { ...n, read: !n.read } : n);
    saveToStorage(updated);
  };

  const totalCount = notifications.length;
  const topNotifications = notifications.slice(0, 3);

  const handleViewAll = () => {
    setIsOpen(false);
    if (type === 'superadmin') {
      navigate('/superadmin-dashboard/notifications');
    } else {
      navigate('/eventmanager-dashboard/notifications');
    }
  };

  return (
    <div className="relative font-sans text-slate-800" ref={containerRef}>
      
      {/* Bell Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-10 h-10 rounded-xl flex items-center justify-center bg-white border border-[#B8A790]/25 text-slate-600 hover:bg-slate-50 hover:text-[#993300] relative transition-all duration-200 outline-none select-none active:scale-95"
      >
        <Bell className="w-4.5 h-4.5" />
        {totalCount > 0 && (
          <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[8px] font-black rounded-full flex items-center justify-center">
            {totalCount}
          </span>
        )}
      </button>

      {/* Dropdown Popup card */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2.5 w-80 bg-white border border-[#B8A790]/35 rounded-2xl shadow-2xl py-4 z-[999] text-left"
          >
            {/* Header info */}
            <div className="px-4 pb-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Bell className="w-4 h-4 text-[#993300]" />
                <span className="text-[10px] font-black text-slate-800 uppercase tracking-wider">Notifications ({totalCount})</span>
              </div>
              <div className="flex gap-2">
                {notifications.length > 0 && (
                  <>
                    <button onClick={markAllRead} className="text-[8px] font-extrabold uppercase text-[#993300] hover:underline select-none">
                      Read All
                    </button>
                    <button onClick={clearAll} className="text-[8px] font-extrabold uppercase text-slate-400 hover:text-red-500 flex items-center gap-0.5 select-none">
                      <Trash className="w-2.5 h-2.5" /> Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* List logs (Top 3) */}
            <div className="max-h-[300px] overflow-y-auto divide-y divide-slate-100">
              {topNotifications.length === 0 ? (
                <div className="py-8 text-center text-slate-450 text-xs italic">
                  No active alerts recorded.
                </div>
              ) : (
                topNotifications.map(n => (
                  <div
                    key={n.id}
                    onClick={() => toggleRead(n.id)}
                    className={`p-3.5 flex items-start gap-3 cursor-pointer transition-all ${
                      n.read ? 'bg-white hover:bg-slate-50' : 'bg-amber-500/5 hover:bg-amber-500/10'
                    }`}
                  >
                    <div className="flex-1 space-y-1">
                      <p className={`text-xs leading-relaxed ${n.read ? 'text-slate-500 font-semibold' : 'text-slate-900 font-extrabold'}`}>
                        {n.message}
                      </p>
                      <span className="text-[8px] text-slate-400 font-medium block">{n.time}</span>
                    </div>
                    {!n.read && (
                      <div className="w-1.5 h-1.5 rounded-full bg-[#993300] mt-1.5" />
                    )}
                  </div>
                ))
              )}
            </div>

            {/* View All Actions Footer */}
            <div className="px-4 pt-3 mt-2 border-t border-slate-100 flex justify-center">
              <button
                onClick={handleViewAll}
                className="w-full py-1.5 text-center text-[10px] font-bold text-white bg-[#993300] hover:bg-[#662200] rounded-xl transition-all select-none uppercase tracking-wider"
              >
                View All Notifications
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
